import { CaretDownFilled, CaretUpFilled } from '@/assets/icons';
import { cn } from '@/utils';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../button';
import { getRenderDropdown, getRenderTextInput } from '../table/config';
import TableWithActions from '../table/table-with-actions';
import { EditableCellType } from '../table/type';
import { useExpandableTable } from './hooks/use-expandable-table';
import { ExpandableTableProps } from './types';

const ExpandableTable = <T extends Record<string, any> = Record<string, any>>({
	columns,
	dataSource,
	containerClassName = '',
	searchComponent,
	config,
	renderGroupHeader,
	renderGroupActions,
	groupHeaderClassName = '',
	groupChildClassName = '',
	showControls = false,
	expandAllText = '펼치기',
	collapseAllText = '닫기',
	autoExpandAll = true,
	tableProps = {},
}: ExpandableTableProps<T>) => {
	const {
		transformedDataSource,
		transformedColumns,
		transformedPagination,
		groupedData,
		groupedRowSelection,
		toggleGroup,
		expandAll,
		collapseAll,
		isGroupExpanded,
	} = useExpandableTable(
		dataSource,
		columns,
		config,
		autoExpandAll,
		tableProps
	);

	const { t } = useTranslation('common');

	const expandCollapseIconNode = useCallback(
		(record: T) => {
			return (
				<div
					className="h-7 flex items-center justify-center gap-2 cursor-pointer"
					onClick={(e) => {
						e.stopPropagation();
						toggleGroup(record[config.groupBy]);
					}}
				>
					{isGroupExpanded(record[config.groupBy]) ? (
						<CaretDownFilled />
					) : (
						<CaretUpFilled fill="#787878" />
					)}
				</div>
			);
		},
		[toggleGroup, config.groupBy, isGroupExpanded]
	);

	// Transform columns với custom logic (hỗ trợ đệ quy cho children)
	const transformColumnsDeep = (
		cols: typeof transformedColumns
	): typeof transformedColumns => {
		return cols.map((column) => {
			const baseColumn = {
				...column,
				render: (
					value: unknown,
					record: T & {
						isGroupHeader?: boolean;
						isGroupChild?: boolean;
						[key: string]: any;
					},
					index: number
				) => {
					if (record?.isGroupHeader) {
						// Custom render cho group header
						if (renderGroupHeader) {
							const group = groupedData.find(
								(g) => g.groupKey === record[config.groupBy]
							);
							if (group) return renderGroupHeader(group);
						}

						// Default render cho group header ở cột tiêu đề
						if (column.dataIndex === config.groupTitleKey) {
							const group = groupedData.find(
								(g) => g.groupKey === record[config.groupBy]
							);
							return (
								<div
									className="w-full font-semibold text-left whitespace-pre absolute left-0 top-1/2 -translate-y-1/2 z-10"
									onClick={(e) => e.stopPropagation()}
								>
									{config.renderGroupHeaderTitle && group ? (
										config.renderGroupHeaderTitle?.(
											group,
											record,
											expandCollapseIconNode(record)
										)
									) : (
										<>
											<span className="text-font2 mr-1">
												{record[config.groupTitleKey]}
											</span>
											<span className="text-blue2">({record.groupCount})</span>
										</>
									)}
								</div>
							);
						}

						// Cột actions (toggle expand/collapse)
						if (column.dataIndex === config.actionsKey) {
							if (config.renderActions)
								return config.renderActions(
									record,
									expandCollapseIconNode(record)
								);
							return expandCollapseIconNode(record);
						}

						// Cột add button
						if (column.dataIndex === config.addButtonKey) {
							if (config.renderAddButton) return config.renderAddButton(record);
							return (
								<div className="flex items-center gap-1 hover:opacity-75">
									<div
										className="cursor-pointer"
										onClick={(e) => {
											e.stopPropagation();
											config.onAddNew?.(record);
										}}
									>
										<span className="text-blue-500">+</span>
									</div>
								</div>
							);
						}

						// Không phải cột đặc biệt: ẩn ở group header
						return null;
					}

					if (column.cellType === EditableCellType.DROPDOWN) {
						return getRenderDropdown(value, record, index, column, t);
					}

					return column.render
						? column.render(value, record, index)
						: getRenderTextInput(value, record, index, column);
				},
				onCell: (
					record: T & {
						isGroupHeader?: boolean;
						isGroupChild?: boolean;
						[key: string]: any;
					},
					index?: number
				) => {
					const originalOnCell = column.onCell;

					const baseProps = originalOnCell
						? originalOnCell(record, index) || {}
						: {};
					if (record?.isGroupHeader) {
						return {
							...baseProps,
							className: cn(
								'[&:not(:last-of-type)]:!border-e-0 !cursor-auto bg-[#F9FCFF]',
								column.dataIndex !== config.groupTitleKey
									? '!border-l-0'
									: '!relative',
								groupHeaderClassName,
								column.className,
								(baseProps as any)?.className
							),
						};
					}
					if (record?.isGroupChild) {
						return {
							...baseProps,
							className: cn(
								groupChildClassName,
								column.className,
								(baseProps as any)?.className
							),
						};
					}
					return {
						...baseProps,
						className: cn(column.className, (baseProps as any)?.className),
					};
				},
			} as typeof column;

			if (column.children && column.children.length) {
				return {
					...baseColumn,
					children: transformColumnsDeep(column.children as typeof columns),
				} as typeof column;
			}
			return baseColumn as typeof column;
		});
	};

	const finalColumns = transformColumnsDeep(transformedColumns);

	return (
		<div className={containerClassName}>
			{/* Header Controls */}
			{showControls && (
				<div className="flex justify-between items-center pt-[15px] pb-2.5 gap-4">
					{searchComponent}
					<div className="flex gap-2">
						<Button
							className="min-w-0"
							onClick={expandAll}
							icon={<CaretDownFilled />}
						>
							{expandAllText}
						</Button>
						<Button
							className="min-w-0"
							onClick={collapseAll}
							icon={<CaretUpFilled fill="#787878" />}
						>
							{collapseAllText}
						</Button>
					</div>
				</div>
			)}

			{/* Table */}
			<TableWithActions
				columns={finalColumns}
				dataSource={transformedDataSource}
				{...tableProps}
				rowSelection={groupedRowSelection}
				pagination={transformedPagination}
				expandable
			/>
		</div>
	);
};

export default memo(ExpandableTable) as <
	T extends Record<string, any> = Record<string, any>,
>(
	props: ExpandableTableProps<T>
) => JSX.Element;
